// Centralised COPPA / RGPD-K tracking policy.
//
// Wellnotes enforces the same rules across every platform:
//   - users under 13 cannot use the app (account must be refused / blocked),
//   - 13–15 year-olds run in "kid mode": no behavioural analytics, no
//     personalised ads, no third-party SDK identifiers,
//   - 16+ users follow the normal consent-based analytics flow.
//
// All call sites that gate analytics, advertising or third-party SDK calls
// should route through this module so the legal contract stays in one place.

import { authState } from "../stores/authStore";

export const MIN_AGE = 13;
export const ADULT_AGE = 16;

/**
 * Returns `'under13' | 'kidMode' | 'adult'` for the supplied age.
 *
 * Anything that isn't a finite positive number is treated as `'adult'` — we
 * cannot enforce a stricter default without locking out anonymous / not-yet-
 * onboarded users from the app entirely. The mobile signup flow is where the
 * age is captured for the first time; once it's stored on the user document
 * the gate applies retroactively to every Analytics call.
 */
export function getAgeBracket(age) {
  const numeric = Number(age);
  if (!Number.isFinite(numeric) || numeric <= 0) return "adult";
  if (numeric < MIN_AGE) return "under13";
  if (numeric < ADULT_AGE) return "kidMode";
  return "adult";
}

/**
 * Behavioural analytics (Firebase Analytics, custom event tracking, future
 * Amplitude integration) may only fire for adults. Both kid-mode minors and
 * under-13 accounts are excluded — under-13 should never reach this code
 * path because they get signed out at boot, but the function still returns
 * `false` defensively.
 */
export function canTrack(age) {
  return getAgeBracket(age) === "adult";
}

/**
 * Personalised advertising (AdMob personalised, AppLovin personalised) is
 * restricted to adults. Web doesn't currently render ads but the helper is
 * exported so future placements share the same gate as iOS / Android.
 */
export function canShowPersonalizedAds(age) {
  return getAgeBracket(age) === "adult";
}

/**
 * Reads the age from the live auth store. Returns `null` when the user
 * document hasn't been hydrated yet — callers should treat `null` as
 * "unknown, don't track" and re-evaluate once `state.userData` arrives.
 */
export function currentUserAge() {
  const data = authState.userData;
  if (!data) return null;
  const age = data.age;
  return typeof age === "number" && Number.isFinite(age) ? age : null;
}

/**
 * Convenience helper for the analytics layer: returns `true` only when we
 * have a hydrated user document AND that user is in the adult bracket. A
 * `null` age (not yet known) blocks tracking until we can confirm the user
 * is old enough — fail-closed is required for COPPA compliance.
 */
export function currentUserCanTrack() {
  const age = currentUserAge();
  if (age == null) return false;
  return canTrack(age);
}

/**
 * Same fail-closed contract for personalised ads.
 */
export function currentUserCanShowPersonalizedAds() {
  const age = currentUserAge();
  if (age == null) return false;
  return canShowPersonalizedAds(age);
}
